<template>
  <el-dialog
    title="血氧戒指批量导入"
    :visible="true"
    width="640px"
    top="0"
    class="ring-import-wrapper"
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="!loading"
    :show-close="!loading"
    :before-close="onClose"
    custom-class="ring-import-dialog"
  >
    <div class="ring-import-content" :aria-busy="loading">
      <p v-if="hospitalChanged" class="ring-import-error" role="alert">
        页面所选机构已变更，请关闭弹窗后重新打开。
      </p>
      <input
        id="ring-import-file-input"
        ref="fileInput"
        type="file"
        accept=".xls,.xlsx"
        aria-label="选择导入的Excel文件"
        hidden
        :disabled="loading"
        @change="onFileChange"
      />
      <div class="ring-import-upload-header">
        <label for="ring-import-file-input">导入文件</label>
        <el-button type="text" size="small" :disabled="loading" @click="downloadTemplate">
          <i class="el-icon-download" aria-hidden="true"></i> 下载Excel模板
        </el-button>
      </div>
      <div class="ring-import-file" :class="{ 'is-selected': !!file }">
        <div class="ring-import-file-icon"><i class="el-icon-document" aria-hidden="true"></i></div>
        <div class="ring-import-file-content">
          <p class="ring-import-filename" :title="file ? file.name : ''">
            {{ file ? file.name : "选择需要导入的Excel文件" }}
          </p>
          <p class="ring-import-file-help">支持 .xls、.xlsx，文件最大 1MB</p>
        </div>
        <el-button type="primary" plain size="small" :disabled="loading || hospitalChanged" @click="$refs.fileInput.click()">
          {{ file ? "重新选择Excel文件" : "选择Excel文件" }}
        </el-button>
      </div>
      <div class="ring-import-guide">
        <p class="ring-import-guide-title"><i class="el-icon-info" aria-hidden="true"></i> 填写说明</p>
        <dl class="ring-import-rules">
          <div><dt>表格格式</dt><dd>读取第一个工作表，第一条非空行为表头。最多 5000 条，表头和空行不计入。</dd></div>
          <div><dt>必填信息</dt><dd>设备编号、姓名、手机号、性别、年龄，每行均须填写。</dd></div>
          <div><dt>设备及手机号</dt><dd>设备编号须为本机构已配置的编号（不是 SN）。设备编号、手机号建议设为文本，保留前导零，且文件内各自不能重复。</dd></div>
          <div><dt>性别及年龄</dt><dd>性别只填写男、女；年龄填写 0～150 的整数。新增患者的生日按导入当天和年龄推算。</dd></div>
          <div><dt>绑定规则</dt><dd>手机号已存在则复用患者，已有资料不修改。设备或患者的旧绑定会解除，再按文件重新绑定。</dd></div>
        </dl>
      </div>
      <div
        v-if="message"
        class="ring-import-result"
        :class="'ring-import-' + messageType"
        role="status"
        aria-live="polite"
      >
        <i :class="resultIcon" aria-hidden="true"></i>
        <span>{{ message }}</span>
      </div>
      <p v-if="loading" class="ring-import-loading" role="status" aria-live="polite">导入中，请等待处理完成。</p>
    </div>
    <template #footer>
      <el-button :disabled="loading" @click="onClose">关闭</el-button>
      <el-button
        type="primary"
        :loading="loading"
        :disabled="loading || !file || hospitalChanged"
        @click="submit"
      >{{ loading ? "导入中" : "开始导入" }}</el-button>
    </template>
  </el-dialog>
</template>

<script>
export default {
  name: "RingImportDialog",
  props: {
    hospital: { type: Object, required: true },
  },
  data() {
    return {
      file: null,
      loading: false,
      message: "",
      messageType: "error",
      templateUrl: process.env.VUE_APP_RING_IMPORT_TEMPLATE_URL ||
        "https://bajie-sleep.oss-cn-hangzhou.aliyuncs.com/%E8%A1%80%E6%B0%A7%E6%88%92%E6%8C%87%E5%88%9D%E7%AD%9B%E7%BB%91%E5%AE%9A%E6%A8%A1%E7%89%88.xlsx",
    };
  },
  computed: {
    resultIcon() {
      return this.messageType === "success" ? "el-icon-success" :
        this.messageType === "warning" ? "el-icon-warning" : "el-icon-error";
    },
    hospitalChanged() {
      return String(this.$store.state.selectedHospital.hospitalid) !==
        String(this.hospital.hospitalid);
    },
  },
  methods: {
    onClose() {
      if (!this.loading) this.$emit("close");
    },
    downloadTemplate() {
      if (this.templateUrl) window.open(this.templateUrl, "_blank", "noopener,noreferrer");
    },
    onFileChange(event) {
      if (this.loading) return;
      const file = event.target.files[0];
      // 允许修正文件后以相同文件名重新选择。
      event.target.value = "";
      if (!file) return;
      this.file = null;
      this.message = "";
      this.messageType = "error";
      if (!/\.(xls|xlsx)$/i.test(file.name)) {
        this.message = "请上传 .xls 或 .xlsx 格式的Excel文件";
      } else if (file.size > 1024 * 1024) {
        this.message = "Excel文件不能超过 1MB";
      } else if (file.size === 0) {
        this.message = "Excel文件为空，请重新选择";
      } else {
        this.file = file;
      }
    },
    async submit() {
      if (this.loading || !this.file || this.hospitalChanged) return;
      if (String(this.hospital.hospitalid) !== "87") {
        this.messageType = "error";
        this.message = "请选择三亚中心医院睡眠医学科";
        return;
      }
      this.loading = true;
      this.message = "";
      try {
        const response = await this.$api.importRingUsers(this.hospital.hospitalid, this.file);
        if (response.code !== "1") {
          this.messageType = "error";
          this.message = response.msg || "导入失败，请修正文件后重新选择并上传";
          return;
        }
        const data = response.data;
        this.messageType = data.syncFailedCount > 0 ? "warning" : "success";
        this.message = `已导入 ${data.totalCount} 条，新增 ${data.createdCount} 人，复用 ${data.existingCount} 人`;
        if (data.syncFailedCount > 0) {
          this.message += `\n第 ${data.syncFailedRows.join("、")} 行的患者信息同步未完成，请联系管理员处理`;
        }
        this.$emit("success", this.hospital.hospitalid);
      } catch (error) {
        const response = error && error.response && error.response.data;
        if (typeof error === "string" || (response && response.code !== "1" && response.msg)) {
          this.messageType = "error";
          this.message = typeof error === "string" ? error : response.msg;
        } else {
          this.messageType = "warning";
          this.message = "请求超时或网络异常，导入结果尚未确认。请先刷新设备和患者列表确认结果，再决定是否重试。";
        }
      } finally {
        this.loading = false;
        this.file = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ring-import-content {
  font-size: 14px;
  line-height: 22px;
  color: #606266;
}
.ring-import-upload-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  color: #303133;
  font-weight: 500;
}
.ring-import-file {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border: 1px solid #dce4ef;
  border-radius: 6px;
  background: #f6f8fc;
  &.is-selected {
    border-color: #99afd5;
  }
}
.ring-import-file-icon {
  flex: 0 0 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #325fac;
  background: #ffffff;
  border-radius: 6px;
  font-size: 28px;
}
.ring-import-file-content {
  flex: 1;
  min-width: 0;
}
.ring-import-file .el-button {
  flex-shrink: 0;
}
.ring-import-file-help {
  margin: 4px 0 0;
  font-size: 12px;
  color: #666666;
}
.ring-import-filename {
  margin: 0;
  overflow-wrap: anywhere;
  min-width: 0;
  color: #303133;
  font-weight: 500;
}
.ring-import-guide {
  margin-top: 24px;
  padding: 18px 20px;
  background: #fafbfc;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
}
.ring-import-guide-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  color: #303133;
  font-weight: 500;
  i {
    color: #325fac;
  }
}
.ring-import-rules {
  margin: 0;
  font-size: 13px;
  > div {
    display: flex;
    gap: 12px;
    & + div {
      margin-top: 12px;
    }
  }
  dt {
    flex: 0 0 78px;
    color: #303133;
  }
  dd {
    margin: 0;
    min-width: 0;
    color: #666666;
  }
}
.ring-import-result {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 14px;
  background: #f5f7fa;
  border-radius: 4px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 160px;
  overflow-y: auto;
  i {
    flex-shrink: 0;
    line-height: 22px;
  }
  span {
    min-width: 0;
  }
}
.ring-import-loading {
  margin: 12px 0 0;
  color: #325fac;
}
.ring-import-error {
  color: #c0392b;
}
.ring-import-warning {
  color: #946000;
}
.ring-import-success {
  color: #267a3b;
}
@media (max-width: 520px) {
  .ring-import-file {
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px;
    > .el-button {
      margin-left: 56px;
    }
  }
  .ring-import-file-content {
    flex-basis: calc(100% - 56px);
  }
  .ring-import-guide {
    padding: 16px;
  }
  .ring-import-rules > div {
    flex-direction: column;
    gap: 4px;
    dt {
      flex-basis: auto;
    }
  }
}
</style>

<style lang="scss">
.ring-import-wrapper {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
}
.ring-import-dialog {
  box-sizing: border-box;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  margin: 0 !important;
  border-radius: 4px;
  .el-dialog__header {
    box-sizing: border-box;
    flex-shrink: 0;
    height: 55px;
    padding: 16px 24px;
    border-bottom: 1px solid #e8e8e8;
  }
  .el-dialog__title {
    font-size: 16px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.85);
  }
  .el-dialog__headerbtn {
    top: 19px;
    right: 24px;
    font-size: 16px;
  }
  .el-dialog__body {
    padding: 24px;
    min-height: 0;
    overflow-y: auto;
  }
  .el-dialog__footer {
    flex-shrink: 0;
    padding: 16px 24px;
    border-top: 1px solid #e8e8e8;
  }
  .el-button:not(.el-button--text) {
    height: 32px;
    padding: 0 14px;
    min-width: 65px;
    font-size: 14px;
    border-radius: 3px;
  }
  .el-button--primary {
    background-color: #325fac;
    border-color: #325fac;
    &:hover,
    &:focus {
      background-color: #476fb4;
      border-color: #476fb4;
    }
    &.is-disabled {
      background-color: #99afd5;
      border-color: #99afd5;
    }
    &.is-plain {
      color: #325fac;
      background: #ffffff;
      border-color: #99afd5;
      &:hover,
      &:focus {
        color: #ffffff;
        background: #325fac;
        border-color: #325fac;
      }
      &.is-disabled {
        color: #909399;
        background: #ffffff;
        border-color: #dcdfe6;
      }
    }
  }
  .el-button--text {
    color: #325fac;
    font-size: 12px;
    padding: 4px 0;
    &.is-disabled {
      color: #909399;
    }
  }
}
</style>
